import type {
  TimingRecommendation,
  TimingRecommendationConditionVerificationCommand,
  TimingRecommendationDecisionCommand,
} from "@reminder-tracker/backend-contracts";
import {
  timingRecommendationAcceptReasons,
  timingRecommendationDeferReasons,
  timingRecommendationRejectReasons,
} from "@reminder-tracker/domain";
import { useMutation, useQuery } from "convex/react";
import { useState } from "react";

import {
  decideTimingRecommendation,
  getGoal2AdministrationCapabilities,
  listTimingRecommendations,
  verifyTimingRecommendationCondition,
} from "../../backend/functions";
import {
  AdministrationAuditDetails,
  AdministrationWorkSurface,
} from "../../ui/administration-surface";

function key(prefix: "corr" | "cmd") {
  return prefix + "_" + crypto.randomUUID().replaceAll("-", "");
}

function sentence(value: string) {
  return value.replaceAll("-", " ").replace(/^./u, (character) => character.toUpperCase());
}

export function TimingRecommendationTasks(): React.JSX.Element | null {
  const [capabilityCorrelationId] = useState(() => key("corr"));
  const projection = useQuery(getGoal2AdministrationCapabilities, {
    correlationId: capabilityCorrelationId,
  });
  const list = useMutation(listTimingRecommendations);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<ReadonlyArray<TimingRecommendation> | null>(null);
  const [selected, setSelected] = useState<TimingRecommendation | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (projection === undefined) return <p role="status">Checking timing-review access…</p>;
  const canRead = projection.capabilities.some(
    ({ capability, granted }) =>
      granted &&
      (capability === "intelligence:read-protected" || capability === "intelligence:decide-timing"),
  );
  const canDecide = projection.capabilities.some(
    ({ capability, granted }) => capability === "intelligence:decide-timing" && granted,
  );
  if (!canRead) return null;

  const load = () => {
    setBusy(true);
    setError(null);
    void list({ correlationId: key("corr") })
      .then((page) => {
        setItems(page.items);
        setTruncated(page.truncated);
      })
      .catch(() => setError("We couldn't read the protected timing-review queue. Try again."))
      .finally(() => setBusy(false));
  };
  if (!open)
    return (
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          load();
        }}
      >
        Review timing recommendations
      </button>
    );
  return (
    <section aria-labelledby="timing-recommendations-heading">
      <button
        type="button"
        onClick={() => {
          setOpen(false);
          setSelected(null);
        }}
      >
        Back to catalogue
      </button>
      <h3 id="timing-recommendations-heading">Timing recommendations</h3>
      <p>
        Protected aggregate choices can raise an editorial question. They do not prove a reminder
        worked, reveal a customer or change a published reminder automatically.
      </p>
      {error === null ? null : <p role="alert">{error}</p>}
      {busy && items === null ? <p role="status">Reading protected recommendations…</p> : null}
      {selected === null ? (
        <>
          {items?.length === 0 ? <p>No timing recommendations need review.</p> : null}
          {items?.map((item) => (
            <TimingRecommendationEvidenceCard
              key={item.recommendationId + "/" + String(item.recommendationVersion)}
              item={item}
              canDecide={canDecide}
              onDecide={() => setSelected(item)}
            />
          ))}
          {truncated ? <p>More recommendations exist. Complete this bounded page first.</p> : null}
          <button type="button" disabled={busy} onClick={load}>
            {busy ? "Refreshing…" : "Refresh queue"}
          </button>
        </>
      ) : selected.state === "deferred" && selected.deferTrigger?.kind === "evidence-condition" ? (
        <TimingConditionVerificationForm
          recommendation={selected}
          onCancel={() => setSelected(null)}
          onComplete={() => {
            setSelected(null);
            setItems(null);
            load();
          }}
        />
      ) : (
        <TimingDecisionForm
          recommendation={selected}
          onCancel={() => setSelected(null)}
          onComplete={() => {
            setSelected(null);
            setItems(null);
            load();
          }}
        />
      )}
    </section>
  );
}

export function TimingRecommendationEvidenceCard({
  item,
  canDecide,
  onDecide,
}: Readonly<{
  item: TimingRecommendation;
  canDecide: boolean;
  onDecide: () => void;
}>): React.JSX.Element {
  return (
    <AdministrationWorkSurface
      id={`timing-recommendation-${item.recommendationId}`}
      title={`${item.presetId} · proposed ${sentence(item.proposedFamilyId)}`}
      purpose="Review the aggregate evidence before making an editorial timing decision."
      state={{
        label: sentence(item.state),
        tone: item.state === "deferred" ? "monitor" : "action",
      }}
      consequence="A decision changes the recommendation review record only. It never changes a published preset or proves a reminder worked."
    >
      <p>
        Preset version {item.presetVersion}; evidence revision {item.evidenceRevision};{" "}
        {item.evidence.numerator} of {item.evidence.denominator} protected workspace-months
        supported this family across {item.evidence.periods.join(", ")}.
      </p>
      <p>
        {item.evidence.uniqueSupportingWorkspaces} of {item.evidence.uniqueEligibleWorkspaces}{" "}
        independent workspaces. Confidence lower bound{" "}
        {new Intl.NumberFormat("en-GB", { style: "percent" }).format(
          item.evidence.confidenceLowerBound,
        )}
        ; completeness{" "}
        {new Intl.NumberFormat("en-GB", { style: "percent" }).format(
          item.evidence.completenessFloor,
        )}
        .
      </p>
      <AdministrationAuditDetails summary="Confounders and public evidence references">
        <p>
          Known confounders:{" "}
          {item.evidence.confounders.length === 0
            ? "None recorded"
            : item.evidence.confounders.map(sentence).join(", ")}
          . Public evidence: {item.publicEvidenceIds.join(", ") || "None recorded"}.
        </p>
      </AdministrationAuditDetails>
      {item.deferTrigger?.kind === "evidence-condition" ? (
        <p>
          Deferred until {sentence(item.deferTrigger.condition)} is verified against reference{" "}
          {item.deferTrigger.reference}. Verification reopens review only.
        </p>
      ) : null}
      {canDecide &&
      item.state === "deferred" &&
      item.deferTrigger?.kind === "evidence-condition" ? (
        <button className="secondary-action" type="button" onClick={onDecide}>
          Verify review condition
        </button>
      ) : canDecide && item.state !== "deferred" ? (
        <button className="primary-action" type="button" onClick={onDecide}>
          Decide this recommendation
        </button>
      ) : (
        <p>You can read this evidence but cannot decide the recommendation.</p>
      )}
    </AdministrationWorkSurface>
  );
}

function TimingConditionVerificationForm({
  recommendation,
  onCancel,
  onComplete,
}: Readonly<{
  recommendation: TimingRecommendation;
  onCancel: () => void;
  onComplete: () => void;
}>): React.JSX.Element {
  const verify = useMutation(verifyTimingRecommendationCondition);
  const [verificationReference, setVerificationReference] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const trigger = recommendation.deferTrigger;
  if (trigger?.kind !== "evidence-condition")
    return <p role="alert">This recommendation no longer has an evidence condition.</p>;
  const send = () => {
    if (!confirmed || busy) return;
    const command: TimingRecommendationConditionVerificationCommand = {
      recommendationId: recommendation.recommendationId,
      recommendationVersion: recommendation.recommendationVersion,
      expectedRevision: recommendation.revision,
      condition: trigger.condition,
      conditionReference: trigger.reference,
      verificationReference,
      commandKey: key("cmd"),
      correlationId: key("corr"),
    };
    setBusy(true);
    setFeedback(null);
    void verify(command)
      .then(() => onComplete())
      .catch(() =>
        setFeedback(
          "The condition or its evidence changed. Refresh and verify the exact current reference.",
        ),
      )
      .finally(() => setBusy(false));
  };
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      <button type="button" onClick={onCancel}>
        Back to recommendations
      </button>
      <h4>Verify review condition for {recommendation.presetId}</h4>
      <p>
        Confirm {sentence(trigger.condition)} for {trigger.reference}. This reopens human review; it
        does not accept, draft, publish or change customer reminders.
      </p>
      <label>
        Verification reference
        <input
          value={verificationReference}
          maxLength={160}
          pattern="[A-Za-z0-9][A-Za-z0-9._:/-]{1,159}"
          required
          onChange={(event) => setVerificationReference(event.target.value)}
        />
      </label>
      <p>Use only a reviewed ticket or evidence identifier—never customer or free-text content.</p>
      <label>
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />{" "}
        I verified this exact condition and understand the recommendation will only become ready for
        another review.
      </label>
      <button type="submit" disabled={!confirmed || busy}>
        {busy ? "Verifying…" : "Mark review due"}
      </button>
      {feedback === null ? null : <p role="status">{feedback}</p>}
    </form>
  );
}

function TimingDecisionForm({
  recommendation,
  onCancel,
  onComplete,
}: Readonly<{
  recommendation: TimingRecommendation;
  onCancel: () => void;
  onComplete: () => void;
}>): React.JSX.Element {
  const decide = useMutation(decideTimingRecommendation);
  const [disposition, setDisposition] = useState<"accept" | "reject" | "defer">("defer");
  const [reason, setReason] = useState<string>(timingRecommendationDeferReasons[0]);
  const [rationale, setRationale] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [conditionTrigger, setConditionTrigger] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);
  const reasons =
    disposition === "accept"
      ? timingRecommendationAcceptReasons
      : disposition === "reject"
        ? timingRecommendationRejectReasons
        : timingRecommendationDeferReasons;
  const changeDisposition = (next: "accept" | "reject" | "defer") => {
    setDisposition(next);
    setReason(
      next === "accept"
        ? timingRecommendationAcceptReasons[0]
        : next === "reject"
          ? timingRecommendationRejectReasons[0]
          : timingRecommendationDeferReasons[0],
    );
    setConfirmed(false);
    setFeedback(null);
  };
  const send = () => {
    if (!confirmed || busy) return;
    let decision: TimingRecommendationDecisionCommand["decision"];
    if (disposition === "accept")
      decision = {
        disposition,
        reason: reason as (typeof timingRecommendationAcceptReasons)[number],
        rationale,
        trigger: null,
      };
    else if (disposition === "reject")
      decision = {
        disposition,
        reason: reason as (typeof timingRecommendationRejectReasons)[number],
        rationale: rationale === "" ? null : rationale,
        trigger: null,
      };
    else {
      const deferReason = reason as (typeof timingRecommendationDeferReasons)[number];
      const reviewAtEpochMilliseconds = Date.parse(reviewDate + "T00:00:00.000Z");
      if (!conditionTrigger && !Number.isFinite(reviewAtEpochMilliseconds)) {
        setFeedback("Choose a future review date within 12 months.");
        return;
      }
      decision = {
        disposition,
        reason: deferReason,
        rationale: rationale === "" ? null : rationale,
        trigger: conditionTrigger
          ? {
              kind: "evidence-condition",
              condition: deferReason,
              reference: "condition:" + deferReason,
            }
          : { kind: "review-date", reviewAtEpochMilliseconds },
      };
    }
    setBusy(true);
    setFeedback(null);
    void decide({
      recommendationId: recommendation.recommendationId,
      recommendationVersion: recommendation.recommendationVersion,
      expectedRevision: recommendation.revision,
      expectedEvidenceRevision: recommendation.evidenceRevision,
      expectedEvidenceDigest: recommendation.evidenceDigest,
      decision,
      commandKey: key("cmd"),
      correlationId: key("corr"),
    })
      .then(() => onComplete())
      .catch(() =>
        setFeedback(
          "The recommendation changed or the decision was not accepted. Refresh and review again.",
        ),
      )
      .finally(() => setBusy(false));
  };
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        send();
      }}
    >
      <button type="button" onClick={onCancel}>
        Back to recommendations
      </button>
      <h4>Decide {recommendation.presetId}</h4>
      <fieldset>
        <legend>Decision</legend>
        {(["accept", "reject", "defer"] as const).map((value) => (
          <label key={value}>
            <input
              type="radio"
              name="timing-decision"
              checked={disposition === value}
              onChange={() => changeDisposition(value)}
            />{" "}
            {sentence(value)}
          </label>
        ))}
      </fieldset>
      <label>
        Reason
        <select value={reason} onChange={(event) => setReason(event.target.value)}>
          {reasons.map((value) => (
            <option key={value} value={value}>
              {sentence(value)}
            </option>
          ))}
        </select>
      </label>
      <label>
        Editorial rationale {disposition === "accept" ? "(required)" : "(optional)"}
        <textarea
          value={rationale}
          maxLength={500}
          required={disposition === "accept" || reason === "other-explanation-required"}
          onChange={(event) => setRationale(event.target.value)}
        />
      </label>
      <p>{rationale.length} of 500 characters. Do not enter customer or personal information.</p>
      {disposition === "defer" ? (
        <fieldset>
          <legend>Review trigger</legend>
          <label>
            <input
              type="checkbox"
              checked={conditionTrigger}
              onChange={(event) => setConditionTrigger(event.target.checked)}
            />{" "}
            Wait for the selected evidence condition
          </label>
          {conditionTrigger ? (
            <p>The condition is {sentence(reason)}. It never approves automatically.</p>
          ) : (
            <label>
              Review date
              <input
                type="date"
                value={reviewDate}
                required
                onChange={(event) => setReviewDate(event.target.value)}
              />
            </label>
          )}
        </fieldset>
      ) : null}
      <label>
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />{" "}
        I reviewed the protected evidence and understand this does not publish or change customer
        reminders.
      </label>
      <button type="submit" disabled={!confirmed || busy}>
        {busy ? "Recording…" : "Record decision"}
      </button>
      {feedback === null ? null : <p role="status">{feedback}</p>}
    </form>
  );
}
